import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface ValueItem {
  icon: string;
  title: string;
  description: string;
}

export interface StatItem {
  value: string;
  label: string;
}

export interface AboutSettings {
  hero: {
    title: string;
    subtitle: string;
    image: string;
  };
  story: {
    sectionTitle: string;
    paragraphs: string[];
    image: string;
  };
  mission: {
    sectionTitle: string;
    mainText: string;
    description: string;
  };
  values: {
    sectionTitle: string;
    items: ValueItem[];
  };
  stats: {
    items: StatItem[];
  };
  cta: {
    title: string;
    subtitle: string;
  };
}

export const defaultAboutSettings: AboutSettings = {
  hero: {
    title: 'Sobre o Miaudecasa',
    subtitle: 'Um espaço criado por quem ama gatos, para quem ama gatos.',
    image:
      'https://readdy.ai/api/search-image?query=Two%20adorable%20cats%20sitting%20together%20on%20cozy%20wooden%20floor%20in%20warm%20sunlit%20home%20interior%2C%20tabby%20and%20orange%20cats%2C%20soft%20golden%20afternoon%20light%2C%20warm%20cream%20background%2C%20professional%20lifestyle%20pet%20photography%2C%20high%20detail%2C%20peaceful%20atmosphere&width=1400&height=600&seq=about-hero-cats-banner-01&orientation=landscape',
  },
  story: {
    sectionTitle: 'Nossa história',
    paragraphs: [
      'O Miaudecasa nasceu em 2020 a partir de um simples amor pelos felinos. Tudo começou quando nossa fundadora, Mariana, adotou seu primeiro gato e percebeu como era difícil encontrar informações confiáveis em português sobre comportamento, saúde e bem-estar felino.',
      'Reunindo especialistas em medicina veterinária, comportamento animal e tutores apaixonados, construímos um portal com o objetivo de transformar a relação entre humanos e gatos, tornando cada lar um lugar mais feliz para essas criaturas incríveis.',
      'Hoje, somos referência para mais de 50 mil tutores que buscam orientações baseadas em ciência, com linguagem acessível e muito carinho.',
    ],
    image:
      'https://readdy.ai/api/search-image?query=Cozy%20orange%20ginger%20cat%20sitting%20on%20warm%20wooden%20desk%20near%20books%20and%20coffee%20cup%2C%20soft%20natural%20light%2C%20home%20office%20atmosphere%2C%20warm%20cream%20and%20amber%20tones%2C%20editorial%20lifestyle%20photography%2C%20high%20detail%2C%20peaceful%20scene&width=700&height=600&seq=about-story-cat-cozy-02&orientation=portrait',
  },
  mission: {
    sectionTitle: 'Nossa missão',
    mainText: 'Tornar cada lar um paraíso felino.',
    description:
      'Acreditamos que um gato feliz começa com um tutor bem informado. Por isso, produzimos conteúdo de qualidade, baseado em evidências, sobre saúde, comportamento, alimentação e bem-estar felino — porque todo gato merece a melhor vida possível.',
  },
  values: {
    sectionTitle: 'Nossos valores',
    items: [
      {
        icon: 'ri-heart-line',
        title: 'Amor pelos felinos',
        description:
          'Cada artigo é escrito por quem genuinamente ama gatos e quer o melhor para esses animais incríveis.',
      },
      {
        icon: 'ri-microscope-line',
        title: 'Ciência e confiança',
        description:
          'Nosso conteúdo é baseado em estudos científicos e revisado por médicos veterinários especializados.',
      },
      {
        icon: 'ri-community-line',
        title: 'Comunidade',
        description:
          'Construímos uma comunidade acolhedora onde tutores trocam experiências e aprendem juntos.',
      },
      {
        icon: 'ri-seedling-line',
        title: 'Bem-estar animal',
        description:
          'Defendemos práticas que respeitam a natureza dos felinos, promovendo seu bem-estar físico e emocional.',
      },
    ],
  },
  stats: {
    items: [
      { value: '50K+', label: 'Tutores mensais' },
      { value: '200+', label: 'Artigos publicados' },
      { value: '6', label: 'Categorias de conteúdo' },
      { value: '4 anos', label: 'De conteúdo felino' },
    ],
  },
  cta: {
    title: 'Faça parte da nossa comunidade',
    subtitle:
      'Junte-se a milhares de tutores que já transformaram a relação com seus gatos através do nosso conteúdo.',
  },
};

function mapRow(row: Record<string, unknown> | null): AboutSettings {
  if (!row) return JSON.parse(JSON.stringify(defaultAboutSettings));

  const parseJson = (val: unknown, fallback: unknown) => {
    if (val === null || val === undefined) return fallback;
    if (typeof val === 'object') return val;
    try {
      return JSON.parse(val as string);
    } catch {
      return fallback;
    }
  };

  return {
    hero: {
      title: (row.hero_title as string) ?? defaultAboutSettings.hero.title,
      subtitle: (row.hero_subtitle as string) ?? defaultAboutSettings.hero.subtitle,
      image: (row.hero_image as string) ?? defaultAboutSettings.hero.image,
    },
    story: {
      sectionTitle: (row.story_section_title as string) ?? defaultAboutSettings.story.sectionTitle,
      paragraphs: parseJson(row.story_paragraphs, defaultAboutSettings.story.paragraphs) as string[],
      image: (row.story_image as string) ?? defaultAboutSettings.story.image,
    },
    mission: {
      sectionTitle: (row.mission_section_title as string) ?? defaultAboutSettings.mission.sectionTitle,
      mainText: (row.mission_main_text as string) ?? defaultAboutSettings.mission.mainText,
      description: (row.mission_description as string) ?? defaultAboutSettings.mission.description,
    },
    values: {
      sectionTitle: (row.values_section_title as string) ?? defaultAboutSettings.values.sectionTitle,
      items: parseJson(row.values_items, defaultAboutSettings.values.items) as ValueItem[],
    },
    stats: {
      items: parseJson(row.stats_items, defaultAboutSettings.stats.items) as StatItem[],
    },
    cta: {
      title: (row.cta_title as string) ?? defaultAboutSettings.cta.title,
      subtitle: (row.cta_subtitle as string) ?? defaultAboutSettings.cta.subtitle,
    },
  };
}

export function useAboutSettings() {
  const [settings, setSettings] = useState<AboutSettings>(defaultAboutSettings);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('about_settings')
        .select('*')
        .eq('id', 1)
        .maybeSingle();

      if (error) throw error;
      setSettings(mapRow(data));
    } catch {
      // keep defaults on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveSettings = async (updated: AboutSettings): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('about_settings')
        .upsert({
          id: 1,
          hero_title: updated.hero.title,
          hero_subtitle: updated.hero.subtitle,
          hero_image: updated.hero.image,
          story_section_title: updated.story.sectionTitle,
          story_paragraphs: updated.story.paragraphs,
          story_image: updated.story.image,
          mission_section_title: updated.mission.sectionTitle,
          mission_main_text: updated.mission.mainText,
          mission_description: updated.mission.description,
          values_section_title: updated.values.sectionTitle,
          values_items: updated.values.items,
          stats_items: updated.stats.items,
          cta_title: updated.cta.title,
          cta_subtitle: updated.cta.subtitle,
          updated_at: new Date().toISOString(),
        });

      if (error) throw error;
      setSettings(updated);
      return true;
    } catch {
      return false;
    }
  };

  return { settings, saveSettings, loading };
}